import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Observable, tap } from 'rxjs';

import { AiConsentState, WITHDRAWAL_ROUTE_LABELS } from '../../../core/attestations/ai-consent.types';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog';
import { IconComponent } from '../../icon/icon';

/**
 * The client's way out of AI-assisted assessment (FLOW-290, POPIA s11(2)).
 *
 * 🚨 **FLOW-297: it withdraws the BUSINESS's consent**, and it is offered to
 * everyone at the business alike — the giver, a director, any member who may
 * give it, the introducing agent (`UI-DA-60-A-02`, `-C-01`). No director claim,
 * no extra step: the server works out and records the route. The copy is a
 * marked PLACEHOLDER (see the template) until the Product Owner's wording lands.
 *
 * Four screens have promised "You can withdraw consent at any time" since the
 * consent was first captured, with nothing behind the sentence. This is the
 * act — rendered next to the promise, on every screen that makes it, so the two
 * cannot drift apart.
 *
 * 🚨 **Withdrawing is a right, not a risk, and the UI must not dress it as
 * one.** The confirm is not styled destructive and the copy does not warn or
 * dissuade: it states the one consequence the client would otherwise discover
 * by surprise — that uploads and assessment stop — and nothing else. There is
 * no "are you sure", no reason field, and no reason asked for anywhere: POPIA
 * s11(2) makes the right unqualified, so asking why would be a soft gate on it.
 *
 * ⚠️ **`null` state renders nothing, and that is not the same as WITHDRAWN.**
 * Null means consent was never asked for on this record — the screen's own
 * consent checkbox is the right control then, not a withdrawal. Rendering
 * "withdrawn" over a never-asked record would report an act nobody performed.
 *
 * The host owns the POST (each product has its own endpoint) and passes it in
 * as `confirm`; the dialog runs it and closes only once it succeeds, so a
 * failed withdrawal leaves the client looking at the error rather than at a
 * screen that claims success.
 */
@Component({
  selector: 'app-ai-consent-withdraw',
  imports: [MatButtonModule, DatePipe, IconComponent],
  templateUrl: './ai-consent-withdraw.html',
  styleUrl: './ai-consent-withdraw.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiConsentWithdrawComponent {
  readonly state = input<AiConsentState | null>(null);

  /**
   * The withdrawal POST, run by the dialog on Confirm.
   *
   * A factory rather than an Observable so nothing fires until the client
   * actually confirms.
   */
  readonly confirm = input.required<() => Observable<unknown>>();

  /**
   * The one consequence stated in the confirm — the business's, since the
   * consent is the business's (FLOW-297). PLACEHOLDER wording, as the template.
   */
  readonly message = input(
    'Uploads and AI assessment stop for this business until consent is given again.',
  );

  /** Emitted once the withdrawal has landed, so the host can re-read its record. */
  readonly withdrawn = output<void>();

  private readonly dialog = inject(MatDialog);

  protected readonly isActive = computed(() => this.state()?.state === 'ACTIVE');
  protected readonly isWithdrawn = computed(() => this.state()?.state === 'WITHDRAWN');
  protected readonly routeLabel = computed(() => {
    const route = this.state()?.withdrawal_route;
    return route ? WITHDRAWAL_ROUTE_LABELS[route] : '';
  });

  protected open(): void {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Withdraw consent?',
        message: this.message(),
        confirmLabel: 'Withdraw consent',
        cancelLabel: 'Cancel',
        confirm: () => this.confirm()().pipe(tap(() => this.withdrawn.emit())),
      },
    });
  }
}
